import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Scale, Smile, Zap, Droplets, CheckCircle, Calendar, ArrowLeft } from 'lucide-react';
import { API } from '@/lib/api';



export default function ProgressPage() {
  const { t, language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = getHeaders();
        const [summaryRes, logsRes] = await Promise.all([
          axios.get(`${API}/progress/summary?days=${period}`, { headers }),
          axios.get(`${API}/tracking/history?days=${period}`, { headers }),
        ]);
        setSummary(summaryRes.data);
        setLogs(logsRes.data.reverse());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [period]);

  const chartData = logs.map(l => ({
    date: l.date?.slice(5),
    weight: l.weight,
    mood: l.mood,
    energy: l.energy,
    water: l.water_glasses,
  }));

  const StatCard = ({ icon: Icon, color, label, value, sub }) => (
    <Card className="bg-white border border-border/60 rounded-2xl p-5 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]">
      <Icon size={20} className={color} />
      <p className="font-body text-xs font-medium text-foreground/60 mt-2">{label}</p>
      <p className="font-display text-3xl font-semibold mt-1">{value}</p>
      {sub && <p className="font-body text-xs text-foreground/40">{sub}</p>}
    </Card>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="font-body text-foreground/40 animate-pulse">{t('common.loading')}</p></div>;
  }

  if (!summary || summary.total_logs === 0) {
    return (
      <div className="text-center py-20" data-testid="progress-empty">
        <Calendar size={48} className="mx-auto text-foreground/20 mb-4" />
        <p className="font-body text-foreground/50">{t('progress.no_data')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 lg:pb-8" data-testid="progress-page">
      <div>
        <button onClick={() => nav('/dashboard')} className="flex items-center gap-1 text-foreground/40 hover:text-foreground font-body text-xs mb-2 transition-colors" data-testid="progress-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('progress.title')}</h1>
        <Select value={period} onValueChange={setPeriod} data-testid="progress-period-select">
          <SelectTrigger className="w-32 rounded-full border-border/80 font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 {language === 'de' ? 'Tage' : 'days'}</SelectItem>
            <SelectItem value="30">30 {language === 'de' ? 'Tage' : 'days'}</SelectItem>
            <SelectItem value="90">90 {language === 'de' ? 'Tage' : 'days'}</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="progress-stats">
        <StatCard icon={Calendar} color="text-foreground/40" label={t('progress.total_days')} value={summary.total_logs} />
        <StatCard icon={Smile} color="text-sage" label={t('progress.mood_trend')} value={summary.mood_avg || '--'} sub="/5" />
        <StatCard icon={Zap} color="text-terracotta" label={t('progress.energy_trend')} value={summary.energy_avg || '--'} sub="/5" />
        <StatCard icon={Droplets} color="text-blue-400" label={t('progress.water_avg')} value={summary.water_avg || '--'} sub={t('dashboard.glasses')} />
        <StatCard icon={CheckCircle} color="text-sage" label={t('progress.habits_rate')} value={`${summary.habits_completion}%`} />
      </div>

      {/* Weight Chart */}
      {summary.weight_data?.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="weight-chart-card">
          <div className="flex items-center gap-2 mb-4">
            <Scale size={18} className="text-terracotta" />
            <h2 className="font-display text-xl font-medium">{t('progress.weight_trend')}</h2>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={summary.weight_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D6" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fontFamily: 'Mulish' }} stroke="#999" tickFormatter={v => v?.slice(5)} />
              <YAxis tick={{ fontSize: 12, fontFamily: 'Mulish' }} stroke="#999" domain={['dataMin - 1', 'dataMax + 1']} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E0D6', fontFamily: 'Mulish' }} />
              <Line type="monotone" dataKey="weight" stroke="#BC563D" strokeWidth={2.5} dot={{ fill: '#BC563D', r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Mood & Energy Chart */}
      {chartData.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="mood-energy-chart-card">
          <h2 className="font-display text-xl font-medium mb-4">{t('progress.mood_trend')} & {t('progress.energy_trend')}</h2>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D6" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fontFamily: 'Mulish' }} stroke="#999" />
              <YAxis domain={[0, 5]} tick={{ fontSize: 12, fontFamily: 'Mulish' }} stroke="#999" />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E0D6', fontFamily: 'Mulish' }} />
              <Area type="monotone" dataKey="mood" stroke="#4A5D4F" fill="#4A5D4F" fillOpacity={0.1} strokeWidth={2} />
              <Area type="monotone" dataKey="energy" stroke="#BC563D" fill="#BC563D" fillOpacity={0.1} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
